import {
  Button, FormControlLabel, Grid, makeStyles, Switch, Tooltip, Typography
} from "@material-ui/core";
import { filter, findIndex, isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { useAuth } from "../../../context/auth";
import { useCommittees } from "../../../hooks/useCommittees";
import { useFormCombos } from "../../../hooks/useFormCombos";
import { actionShowSpinner } from "../../../redux-flow/action/Wrapper/Spinner";
import { disabledCommittee } from "../../../services/committees.service";
import { getAll } from "../../../services/committePackages.service";
import DialogCustom from '../../Common/DialogCustom';
import ToastMessage from "../../Common/ToastMessage";
import { checkAccess } from "../utils/index";
import Info from "./Info";
import Members from "./Members";
import PackageCommittee from "./PackageCommittee";
import Period from "./Period";
import Quorum from "./Quorum";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1vh 1vw"
  },
  subtitle: {
    ...theme.typography.subtitle1
  },
  button: {
    fontSize: "1rem",
    fontWeight: "bold",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50
  },
  buttonOutlined: {
    border: "2px solid",
    fontSize: "1rem",
    fontWeight: "bold",
    color: theme.palette.grey.main,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50
  },
  spacer: {
    marginBottom: 20
  }
}));

const CommitteePage = ({
  t,
  history,
  match,
  actionShowSpinner,
  disabled = false,
}) => {
  const classes = useStyles();
  const { userAccess } = useAuth();

  const {
    params: { id },
  } = match;

  const [dataApiRequestInfo, setDataApiRequestInfo] = useState({});
  const [dataApiRequestPeriod, setDataApiRequestPeriod] = useState({});
  const [dataApiRequestQuorum, setDataApiRequestQuorum] = useState({});
  const [dataApiRequestMembers, setDataApiRequestMembers] = useState({
    memberListSelected: [],
  });
  const [allMembers, setAllMembers] = useState([]);
  const [oneTime, setOneTime] = useState(false);
  const [fillEdit, setFillEdit] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    content: "",
    type: "",
    autoHideDuration: 4000,
  });
  const [confidential, setConfidential] = useState(false);
  const [disable, setDisable] = useState(false);
  const [checkDisabled, setCheckDisabled] = useState(false);

  const [canConfidentializeCommittee, setCanConfidentializeCommittee] = useState(false);
  const [canCreateCommittee, setCanCreateCommittee] = useState(false);
  const [canUpdateCommittee, setCanUpdateCommittee] = useState(false);
  const [canDisableCommittee, setCanDisableCommittee] = useState(false);
  const [selectedPackageCommittee, setSelectedPackageCommittee] = useState();
  const [committeePackageOptions, setCommitteePackageOptions] = useState([]);

  const { getMembers, getMembersByPartialName, setMembersList, membersList, filteredList } = useFormCombos();
  const {
    getCommittee,
    committee,
    success,
    error,
    create,
    update,
  } = useCommittees({
    spinner: actionShowSpinner,
  });

  useEffect(() => {
    getAll().then(({committeePackages}) => {
      setCommitteePackageOptions(committeePackages)
      if (userAccess) {
        const hasAccess = checkAccess(userAccess, 'listCommittees');
        const hasAccessToConfidentializeCommittee = checkAccess(userAccess, 'confidentializeCommittee');
        const hasAccessToCreateCommittee = checkAccess(userAccess, 'createCommitte');
        const hasAccessToUpdateCommittee = checkAccess(userAccess, 'updateCommittee');
        const hasAccessToDisableCommittee = checkAccess(userAccess, 'disableCommittee');
  
        setCanConfidentializeCommittee(hasAccessToConfidentializeCommittee);
        setCanCreateCommittee(hasAccessToCreateCommittee);
        setCanUpdateCommittee(hasAccessToUpdateCommittee);
        setCanDisableCommittee(hasAccessToDisableCommittee);
  
        if (!hasAccess) {
          history.push('/');
        }
  
        getMembers();
  
        if (id && committee) {
          getCommittee(id);
          if (committee == [] || committee == null || committee.length) {
            committee = []
          }
        }
      }
    });
  }, [id, userAccess]);

  useEffect(() => {
    if (membersList.length > 0 && !oneTime) {
      setAllMembers(membersList);
      setOneTime(true);
    }
  }, [membersList]);

  useEffect(() => {
    if (success.disable === "disable") {
      setMessage({
        open: true,
        content: "Status alterado com sucesso!",
        type: "success",
      });
    }
    if (success.create === "create") {
      history.push({ pathname: "/committees", state: { successCreateCommitee: true } });
    }
    if (success.update === "update") {
      history.push({ pathname: "/committees", state: { successEditCommitee: true } });
    }
  }, [success]);

  useEffect(() => {
    if (error.create) {
      setMessage({
        open: true,
        content: error.create,
        type: "error",
        autoHideDuration: null,
      });
    }
    if (error.update) {
      setMessage({
        open: true,
        content: error.update,
        type: "error",
        autoHideDuration: null,
      });
    }
  }, [error]);

  useEffect(() => {
    if (id && !isEmpty(committee) && allMembers.length > 0 && !fillEdit) {
      const props = ["id", "name", "email"];
      const result = allMembers
        .filter((o1) => !committee.members.some((o2) => o1.id === o2.id))
        .map((o) => {
          return props.reduce(function (newo, name) {
            newo[name] = o[name];
            return newo;
          }, {});
        });
      setMembersList(result);
      setFillEdit(true);
      setDataApiRequestMembers({
        ...dataApiRequestMembers,
        memberListSelected: committee.members.map((item) => ({
          id: item.id,
          isHaveRightToVote: item.isHaveRightToVote,
        })),
      });
    }
    if (id && !isEmpty(committee)) {
      setConfidential(committee.internal);
      setDisable(committee.activated);
      setSelectedPackageCommittee({
        id: committee.committeePackageId,
        name: committee.committeePackageName,
        admin: false
      })
    }
  }, [committee, allMembers]);

  const handleSearchMember = (name) => {
    getMembersByPartialName('pt', name);
  }

  const handleChangeDataPeriod = (data) => {
    setDataApiRequestPeriod({ ...dataApiRequestPeriod, ...data });
    setMessage({
      open: false,
      content: "",
      type: "",
      autoHideDuration: 4000,
    });
  };

  const handleChangeDataQuorum = (data) => {
    setDataApiRequestQuorum({ ...dataApiRequestQuorum, ...data });
    setMessage({
      open: false,
      content: "",
      type: "",
      autoHideDuration: 4000,
    });
  };

  const handleChangeDataInfo = (data) => {
    setDataApiRequestInfo({ ...dataApiRequestInfo, ...data });
    setMessage({
      open: false,
      content: "",
      type: "",
      autoHideDuration: 4000,
    });
  };

  const handleChangeDataMembers = (data, type) => {
    const newMemberListSelected = [...dataApiRequestMembers.memberListSelected];
    if (type === "add") newMemberListSelected.push(data);
    else {
      const idx = findIndex(newMemberListSelected, ["id", data.id]);
      newMemberListSelected.splice(idx, 1);
    }
    setDataApiRequestMembers({
      memberListSelected: newMemberListSelected,
    });
  };

  const handleChangeConfidential = (event) => {
    setConfidential(event.target.checked);
  };

  const handleChangeDisable = async (event) => {
    const disableCommitteeResponse = await disabledCommittee({ id, disabled: checkDisabled });
    setOpen(false);

    if(disableCommitteeResponse.error.length > 0) {
      setMessage({
        open: true,
        type: "error",
        autoHideDuration: 4000,
        content: disableCommitteeResponse.error.length > 1 ? disableCommitteeResponse.error : t("menu.committee.disableError"),                                                                                                                                                                                                                       
      });

      return;
    }      

    setDisable(event.target.checked);

    history.push({
      pathname: "/committees",
      state: disable
        ? { successDisabledCommitee: true }
        : { successEnabledCommitee: true }
    });
  };

  const handleSave = async () => {
    actionShowSpinner(true);
    setMessage({
      open: false,
      content: "",
      type: "",
      autoHideDuration: 4000,
    });

    const [durationHours, durationMinutes] = dataApiRequestPeriod?.duraction?.split(':');

    const durationValid = durationHours.length === 2 && durationMinutes.length === 2;

    if (!durationValid && durationHours.length > 0) {
      setMessage({
        open: true,
        content: "Duração inválida",
        type: "error",
        autoHideDuration: 4000,
      });

      actionShowSpinner(false);
      return;
    }

    const request = {
      members: dataApiRequestMembers.memberListSelected,
      name: dataApiRequestInfo.nameCommittee,
      email: dataApiRequestInfo.emailCommittee,
      chairPerson: (dataApiRequestInfo.chairperson && dataApiRequestInfo.chairperson.id) || "",
      cochair: dataApiRequestInfo.coChair ? dataApiRequestInfo.coChair.id : null,
      secretary: (dataApiRequestInfo.secretary && dataApiRequestInfo.secretary.id) || "",
      subordinated: (dataApiRequestInfo.subordinate && dataApiRequestInfo.subordinate.id) || null,
      frequency: (dataApiRequestPeriod?.frequency && dataApiRequestPeriod?.frequency.id) || "",
      daysOfWeek: dataApiRequestPeriod.daysOfWeek,
      duration: dataApiRequestPeriod.duraction,
      internal: confidential,
      countInstalers: (dataApiRequestQuorum.installation && dataApiRequestQuorum.installation) || "0",
      countApprovers: (dataApiRequestQuorum.approval && dataApiRequestQuorum.approval) || "0",
      committeePackageId: selectedPackageCommittee.id ?? "",
      minimumDeadlineDays: dataApiRequestInfo.minimumDeadlineDays ? Number(dataApiRequestInfo.minimumDeadlineDays) : null,
    };
    
    if (!id) {
      if (canCreateCommittee) {
        await create(request);

        actionShowSpinner(false);
      }
    }
    else {
      if (canUpdateCommittee) {
        await update(id, request);

        actionShowSpinner(false);
      }
    }
  };

  const [open, setOpen] = useState(false);

  const handleClickOpen = (event) => {
    setCheckDisabled(event.target.checked);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Grid container className={classes.root}>
      <Grid container item direction="row" justify="space-between" className={classes.spacer}>
        <Grid item xs={12} md={6} lg={6}>
          <Typography variant="h4">
            {disabled ? t('forms.committee.preview') : t(`forms.committee.${id ? "edit" : "new"}`)}
          </Typography>
        </Grid>
        <Grid item xs={6} md={3} lg={3}>
          {!disabled &&
            (
              <FormControlLabel
                control={
                  <Switch
                    checked={confidential}
                    onChange={handleChangeConfidential}
                    name="confidential"
                    color="primary"
                    disabled={disabled || !canConfidentializeCommittee}
                  />
                }
                label={t("confidential")}
                className={classes.subtitle}
              />
            )}
        </Grid>
        {id && (
          <Grid item xs={6} md={3} lg={3}>
            {!disabled &&
              (
                <FormControlLabel
                  control={
                    <Tooltip title={disable ? t("deactivate") : t("activate")}>
                      <Switch
                        checked={disable}
                        onChange={handleClickOpen}
                        name="disable"
                        color="primary"
                        disabled={disabled || !canDisableCommittee}
                        tool
                      />
                    </Tooltip>
                  }
                  label={disable ? t("active") : t("inactive")}
                  className={classes.subtitle}
                />
              )}
          </Grid>
        )}
        <div>
          <DialogCustom
            open={open}
            handleClose={handleClose}
            handleChange={handleChangeDisable}
            title={t(`forms.dialog.alert`)}
            body={disable ? t("forms.dialog.textInactivate") : t("forms.dialog.textActivate")}
            confirmText={t(`forms.dialog.yes`)}
            cancelText={t(`forms.dialog.no`)}
            alert={true}
          />
        </div>
      </Grid>
      <Grid container item direction="row" spacing={2}>
        <Grid container item md={8} lg={8}>
          <Grid item xs={12} md={12} lg={12}>
            <Info
              onChangeData={handleChangeDataInfo}
              committee={committee}
              handleSearchMember={handleSearchMember}
              filteredList={filteredList}
              spinner={actionShowSpinner}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <Period
              onChangeData={handleChangeDataPeriod}
              committee={committee}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <Quorum
              onChangeData={handleChangeDataQuorum}
              committee={committee}
              disabled={disabled}
            />
          </Grid>
        </Grid>
        <Grid container item md={4} lg={4} direction="column">
          <Grid item>
            <Members
              committeeMembers={filter(committee.members, [
                "isHaveRightToVote",
                true,
              ])}
              title={t("forms.committee.membersVote.title")}
              onChangeData={handleChangeDataMembers}
              handleSearchMember={handleSearchMember}
              filteredList={filteredList}
              disabled={disabled}
              voto={true}
              dataApiRequestMembers={dataApiRequestMembers}
            />
          </Grid>
          <Grid item>
            <Members
              committeeMembers={filter(committee.members, [
                "isHaveRightToVote",
                false,
              ])}
              title={t("forms.committee.members.title")}
              onChangeData={handleChangeDataMembers}
              handleSearchMember={handleSearchMember}
              filteredList={filteredList}
              voto={false}
              disabled={disabled}
              dataApiRequestMembers={dataApiRequestMembers}
            />
          </Grid>
          <Grid item>
            <PackageCommittee
              title={t("softwareManagement.modules.committeePackage.title")}
              setSelectedPackageCommittee={setSelectedPackageCommittee}
              selectedPackageCommittee={selectedPackageCommittee}
              packageCommitteOptions={committeePackageOptions}
              disabled={disabled}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid container item direction="row" justify="center" spacing={2} >
        <Grid item>
          <Button
            variant="outlined"
            className={classes.buttonOutlined}
            onClick={() => history.push("/committees")}
          >
            {t(`${disabled ? "voltar" : "cancel"}`)}
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            className={classes.button}
            onClick={handleSave}
            disabled={(disabled) || (!canUpdateCommittee && !canCreateCommittee)}
          >
            {t("saveCommittee")}
          </Button>
        </Grid>
      </Grid>
      <ToastMessage
        message={message.content}
        type={message.type}
        open={message.open}
        autoHideDuration={message.autoHideDuration}
      />
    </Grid>
  );
};

const withRouterCommitteePage = withRouter(CommitteePage);

const mapDispatchToProps = (dispatch) => ({
  actionShowSpinner: (request) => dispatch(actionShowSpinner(request)),
});

const CommitteePageConnect = connect(
  null,
  mapDispatchToProps
)(withRouterCommitteePage);

export default withTranslation()(CommitteePageConnect);
